import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Auth0Metadata } from '../../api/API';
import { CourseGroupV2, CourseType } from '../../api/models/Course';
import { Student } from '../../api/models/Student';
import { Environment } from '../../Environment';
import { RootState } from '../../model/store';
import { studentSlice } from '../../model/studentSlice';
import { useCourseGroups } from '../../util/CourseGroupsHook';
import { useToken } from '../../util/GetTokenHook';
import { history } from '../../util/History';
import { Auth0Context, useAuth0 } from '../Auth0Provider';

export type CourseDetailsProps = {
  // Outputs
  /** Course Group based on url parameter :course */
  courses: CourseGroupV2[];
  /** as the name implies */
  isGettingCourseGroups: boolean;
  /** current page */
  currentPage: number;
  /** current student to get credits from */
  student: Student | undefined;
  /** as the name implies */
  isGettingStudent: boolean;

  // Inputs
  backToOverview: () => void;
  userPressedCourseDetails: (courseType: CourseType) => void;
  userClickedPaginationDot: (currentPage: number) => void;
};

export const courseDetailsContainer = (CourseDetailsScreen: React.ComponentType<CourseDetailsProps>) => () => {
  const [courses, setCourses] = React.useState<CourseGroupV2[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isGettingStudent, setIsGettingStudent] = useState(false);

  const { api } = Environment.current();
  const { user } = useAuth0() as Auth0Context;
  const { course }: { course: CourseType } = useParams();

  const student = useSelector((state: RootState) => state.student.profile);
  const personId = user?.[Auth0Metadata.PersonId];
  const dispatch = useDispatch();

  // custom hooks
  const { token, isGettingToken } = useToken();
  // difference with courses above: courseGroups exist so that
  // we don't have to refetch academic check everytime a course detail button is clicked
  const { courseGroups, isGettingCourseGroups } = useCourseGroups({ token, personId });

  // get courses from course groups
  React.useEffect(() => {
    if (course && courseGroups.length) {
      const courseType = course.toUpperCase();
      const filteredCourses = courseGroups.filter(group => group.courseGroupCode === courseType);
      setCourses(filteredCourses);
    }
  }, [course, courseGroups.length]);

  // get student if it doesn't yet exist
  React.useEffect(() => {
    if (token && personId && !student?.id) {
      (async () => {
        try {
          setIsGettingStudent(true);
          const result = await api.getStudentById(personId, token);
          dispatch(studentSlice.actions.receivedStudent({ student: result }));
          setIsGettingStudent(false);
        } catch (e) {
          toast.error(e.message);
          setIsGettingStudent(false);
        }
      })();
    }
  }, [token, personId, student?.id]);

  return (
    <CourseDetailsScreen
      student={student}
      currentPage={currentPage}
      isGettingCourseGroups={isGettingCourseGroups || isGettingToken}
      isGettingStudent={isGettingStudent || isGettingToken}
      courses={courses}
      backToOverview={() => {}}
      userClickedPaginationDot={setCurrentPage}
      userPressedCourseDetails={(courseType: CourseType) => {
        setCurrentPage(0);
        history.push(`/academic-overview/${courseType.toLowerCase()}`);
      }}
    />
  );
};
